import React from 'react';
import { Link } from 'react-router';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import {
  AbsenceIllustration,
  HomeworkIllustration,
  NewsIllustration,
  TestScheduleIllustration,
} from '@/components/Icons/HeaderIcons';
import { HeaderCardIcon, type IconComponentProps } from '@/types/components';
import { tv } from '@/utils/tv';

const iconComponents: Record<HeaderCardIcon, React.FC<IconComponentProps>> = {
  [HeaderCardIcon.Absence]: AbsenceIllustration,
  [HeaderCardIcon.News]: NewsIllustration,
  [HeaderCardIcon.Homework]: HomeworkIllustration,
  [HeaderCardIcon.TestSchedule]: TestScheduleIllustration,
};

const headerCard = tv({
  base: 'flex flex-1 cursor-pointer items-center justify-between gap-2 overflow-hidden border border-transparent transition-opacity hover:opacity-90 contrast:border-border',
  variants: {
    isCompact: {
      true: 'h-11 rounded-xl py-3 pr-2 pl-3 md:w-[180px] md:flex-none',
      false: 'h-14 rounded-2xl p-3 md:max-w-[260px] md:flex-none',
    },
  },
});

const headerCardIconGroup = tv({
  base: 'flex min-w-0 items-center gap-3',
});

const headerCardIcon = tv({
  base: 'size-12 max-w-none shrink-0',
});

interface Props {
  label: string;
  icon?: HeaderCardIcon;
  bgClassName: string;
  iconClassName?: string;
  iconGapClassName?: string;
  className?: string;
  badgeCount?: number;
  isCompact?: boolean;
  to?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const HeaderCard: React.FC<Props> = ({
  label,
  icon,
  iconClassName,
  iconGapClassName,
  className,
  bgClassName,
  badgeCount,
  isCompact = false,
  to,
  onClick,
}) => {
  const cardClassName = headerCard({
    isCompact,
    className: `${bgClassName} ${className ?? ''}`,
  });
  const Icon = icon && iconComponents[icon];

  const content = (
    <>
      <div className={headerCardIconGroup({ className: iconGapClassName })}>
        {Icon && (
          <span className="hidden size-8 shrink-0 items-center justify-center overflow-hidden lg:flex">
            <Icon className={headerCardIcon({ className: iconClassName })} />
          </span>
        )}
        <span
          className={`truncate text-foreground ${isCompact ? 'text-subhead-14' : 'text-[15px] leading-5 font-medium'}`}
        >
          {label}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!!badgeCount && badgeCount > 0 && (
          <span className="flex size-[18px] items-center justify-center rounded-full bg-accent text-caption-10 font-semibold text-accent-foreground">
            {badgeCount}
          </span>
        )}
        <ChevronRight
          strokeWidth="2"
          className={`text-foreground ${isCompact ? 'size-4' : 'size-3.5'}`}
        />
      </div>
    </>
  );

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={cardClassName}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={cardClassName}>
      {content}
    </button>
  );
};
